import { defineCrestModule } from '@crest/platform-contracts/modules';
import { assistantActionProviderHostCapability, assistantProposalWorkflowCapability, } from './contracts/assistant-proposal.contract.js';
import { assistantContextProviderCapability } from './contracts/assistant-context.contract.js';
import { assistantModelProviderHostCapability, assistantTurnLifecycleCapability, } from './contracts/assistant-turn.contract.js';
import { assistantActionExecutionCapability } from './contracts/assistant-action-execution.contract.js';
import { platformApprovedActionInvocationReceiptCapability } from '@crest/platform-contracts/hosts';
export const assistantModuleManifest = defineCrestModule({
    id: 'assistant', version: '1.0.0', kind: 'business', required: false,
    requires: ['platform'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        platformApprovedActionInvocationReceiptCapability,
    ],
    optionalCapabilities: [], optionalCapabilityFallbacks: [],
    provides: [
        assistantActionProviderHostCapability, assistantProposalWorkflowCapability,
        assistantContextProviderCapability, assistantModelProviderHostCapability,
        assistantTurnLifecycleCapability, assistantActionExecutionCapability,
    ],
    permissions: [
        'assistant.proposals.read', 'assistant.proposals.request', 'assistant.proposals.approve',
        'assistant.proposals.execute',
        'assistant.conversations.read', 'assistant.conversations.write', 'assistant.conversations.archive',
    ],
    settings: [
        'assistant.context.redaction-policy', 'assistant.display.workspace', 'assistant.copy.workspace',
    ],
    copyCatalogs: ['assistant.copy.workspace'],
    events: {
        publishes: [
            'assistant.proposal-created.v1', 'assistant.proposal-approved.v1', 'assistant.proposal-rejected.v1',
            'assistant.turn-queued.v1', 'assistant.turn-completed.v1', 'assistant.turn-failed.v1',
            'assistant.turn-cancelled.v1',
            'assistant.action-queued.v1', 'assistant.action-completed.v1',
            'assistant.action-failed.v1', 'assistant.action-cancelled.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'assistant.routes.proposals', priority: 0 },
            { id: 'assistant.routes.conversations', priority: 0 },
            { id: 'assistant.routes.context', priority: 0 },
            { id: 'assistant.routes.turns', priority: 10 },
            { id: 'assistant.routes.action-executions', priority: 20 },
            { id: 'assistant.routes.legacy-project-updates', priority: 30 },
        ],
        jobs: [{
                id: 'assistant.jobs.process-turns',
                requiredCapability: assistantModelProviderHostCapability,
                priority: 10,
            }, {
                id: 'assistant.jobs.process-actions',
                priority: 20,
            }], subscribers: [], slots: [],
    },
    web: {
        routes: [{ id: 'assistant.web.workspace', requiredPermission: 'assistant.conversations.read', priority: 0 }],
        navigation: [{ id: 'assistant.navigation.workspace', requiredPermission: 'assistant.conversations.read', priority: 70 }],
        slots: [{ id: 'assistant.web.settings.redaction-policy', priority: 0 }],
    },
    mobile: { routes: [], slots: [] }, portal: { routes: [], slots: [] },
    database: {
        schema: 'assistant',
        owns: [
            'assistant.proposals', 'assistant.proposal_events', 'assistant.proposal_commands',
            'assistant.conversations', 'assistant.conversation_messages', 'assistant.conversation_commands',
            'assistant.turns', 'assistant.turn_events', 'assistant.turn_commands',
            'assistant.action_executions', 'assistant.action_execution_events',
            'assistant.action_execution_commands', 'assistant.claim_queued_action_executions',
            'assistant.fail_claimed_action_execution', 'assistant.complete_claimed_action_execution',
            'assistant.guard_proposal_provider_contract_identity_v1',
            'assistant.guard_action_execution_provider_contract_identity_v1',
            'assistant.issue_approved_action_invocation_receipt_v1',
            'assistant.assert_approved_action_invocation_receipt_consumed_v1',
            'assistant.apply_claimed_action_execution_terminal_state_v1',
            'assistant.complete_claimed_receipt_bound_action_execution_v1',
        ],
        readsThrough: [
            'platform.tenant-context.v1',
            'platform.module-access.v1',
            platformApprovedActionInvocationReceiptCapability,
        ],
        migrations: [
            'assistant.proposal-workflow', 'assistant.conversation-context',
            'assistant.turn-lifecycle', 'assistant.action-execution',
            'assistant.approved-action-invocation-receipts',
            'assistant.insurance-claims-provider-contract-upgrade',
        ],
    },
    health: ['assistant.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 32, jobConcurrency: 1, queueBacklogLimit: 100,
            databaseConnectionBudget: 8, workerIsolation: 'separate-worker',
        },
        disable: {
            data: 'retain', clientContributions: 'remove', serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip', events: {
                behavior: 'drop-safe', durableCursor: false, retentionDays: 0, backlogLimit: 0,
                warningAt: 0, reconciliationProcedure: null,
                dropSafeReason: 'Assistant proposals are retained records and have no subscriber backlog in this slice.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send', inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published', healthChecks: ['assistant.database'],
            reconciliationProcedure: null, visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove', data: 'retain', tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Assistant proposal, conversation, turn, action-execution, and decision evidence remains retained while the module is disabled.',
});
//# sourceMappingURL=assistant.module.js.map